import { X } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/ui/Button.js';
import type { ScheduleBar } from '../types.js';
import { barStyleFor } from './barStyle.js';
import { mytPeriod } from './dropLogic.js';
import { usePopoverShell } from './usePopoverShell.js';

/**
 * 316 (FR-015) — a block's details, portalled beside it. Until 316 they opened in a panel inserted
 * ABOVE the grid, which pushed every row down the moment a block was clicked. Same actions and gates
 * as that panel: save the reason, remove (with `delete CarBlock`), close.
 */
export function BlockDetailsPopover({
  bar,
  carLabel,
  anchorRect,
  canRemove,
  onSaveReason,
  onRemove,
  onDismiss,
}: {
  bar: ScheduleBar;
  carLabel: string;
  anchorRect: DOMRect;
  canRemove: boolean;
  onSaveReason: (reason: string) => void;
  onRemove: () => void;
  onDismiss: () => void;
}) {
  const { boxRef, style } = usePopoverShell(anchorRect, onDismiss);
  const [reason, setReason] = useState(bar.reason ?? '');
  return createPortal(
    <div
      ref={boxRef}
      data-testid="block-details-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={`${barStyleFor(bar).word} on ${carLabel}`}
      style={style}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-ink">{barStyleFor(bar).word}</p>
          <p className="text-xs text-steel">{carLabel}</p>
          <p className="text-xs text-ink">
            {mytPeriod(Date.parse(bar.start_at), Date.parse(bar.end_at))}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <label className="flex flex-col gap-1 text-xs text-steel">
        <span>Reason</span>
        <input
          aria-label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
        />
      </label>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => onSaveReason(reason)}>
          Save reason
        </Button>
        {canRemove && (
          <Button variant="danger" onClick={onRemove}>
            Remove block
          </Button>
        )}
      </div>
    </div>,
    document.body,
  );
}
